import SectionMessage from '@atlaskit/section-message';
import ChevronRightLargeIcon from '@atlaskit/icon/glyph/chevron-right-large';
import ChevronDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import t from 'modules/i18n/intl';
import { ATLASSIAN_TOGETHER, atlassianAccessKeys } from 'data/products/keys';
import { OverviewEntity } from '../duck/models';
import { isEnterpriseOrgProduct } from '../../../util/mac/helpers';
import { StyledLink } from './licensesExpandedRow/StyledComponents';

const CollapsingList = styled.ul<{ isCollapsed: boolean }>`
  display: ${({ isCollapsed }) => (isCollapsed ? 'none' : 'block')};
`;

const SubscriptionSection = ({ titleId, defaultMessage, items, isCollapsed, onToggle }) => (
  <>
    <p onClick={onToggle} role="presentation">
      <StyledLink>
        {isCollapsed ? (
          <ChevronRightLargeIcon label="" size="small" />
        ) : (
          <ChevronDownIcon label="" size="small" />
        )}
        <FormattedMessage id={titleId} defaultMessage={defaultMessage} />
      </StyledLink>
    </p>
    <CollapsingList isCollapsed={isCollapsed}>
      {items.map((acc) => (
        <li key={`sen-${acc.accountId}`}>SEN-{acc.accountId}</li>
      ))}
    </CollapsingList>
  </>
);

const EnterpriseSubscriptionsBanner = ({
  defaultLicensesItems,
}: {
  defaultLicensesItems: OverviewEntity[];
}) => {
  const [withEnterpriseProducts, setWithEnterpriseProducts] = useState<OverviewEntity[]>([]);
  const [withAtlassianTogether, setWithAtlassianTogether] = useState<OverviewEntity[]>([]);
  const [withBothProducts, setWithBothProducts] = useState<OverviewEntity[]>([]);

  const [collapsedStates, setCollapsedStates] = useState({
    enterprise: true,
    atlassianTogether: true,
    both: true,
  });

  const handleCollapseClick = (key) => {
    setCollapsedStates((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };

  useEffect(() => {
    if (!defaultLicensesItems) {
      return;
    }
    const orgChildren = defaultLicensesItems.filter(
      (acc) => acc.parentAccountId !== null && !atlassianAccessKeys.includes(acc.productKey),
    );

    const orgAccountsMap = new Map<number, OverviewEntity[]>();

    orgChildren.forEach((it) => {
      if (!orgAccountsMap.has(it.parentAccountId!)) {
        orgAccountsMap.set(it.parentAccountId!, []);
      }
      orgAccountsMap.get(it.parentAccountId!)!.push(it);
    });

    const accessWithOnlyEnterpriseProducts: OverviewEntity[] = [];
    const accessWithOnlyAtlassianTogetherProducts: OverviewEntity[] = [];
    const accessWithEnterpriseAndAtlassianProducts: OverviewEntity[] = [];

    orgAccountsMap.forEach((org, parentId) => {
      const accessAccount = defaultLicensesItems.find((it) => it.accountId === parentId);
      if (accessAccount) {
        const hasEnterpriseOrgProducts = org.some((it) => isEnterpriseOrgProduct(it.productKey));
        const hasAtlassianTogetherOrgProduct = org.some(
          (it) => ATLASSIAN_TOGETHER === it.productKey,
        );

        if (hasAtlassianTogetherOrgProduct && !hasEnterpriseOrgProducts) {
          accessWithOnlyAtlassianTogetherProducts.push(accessAccount);
        }
        if (hasEnterpriseOrgProducts && !hasAtlassianTogetherOrgProduct) {
          accessWithOnlyEnterpriseProducts.push(accessAccount);
        }
        if (hasAtlassianTogetherOrgProduct && hasEnterpriseOrgProducts) {
          accessWithEnterpriseAndAtlassianProducts.push(accessAccount);
        }
      }
    });

    setWithAtlassianTogether(accessWithOnlyAtlassianTogetherProducts);
    setWithEnterpriseProducts(accessWithOnlyEnterpriseProducts);
    setWithBothProducts(accessWithEnterpriseAndAtlassianProducts);
  }, [defaultLicensesItems?.length]);

  const hasSubscriptions =
    withEnterpriseProducts.length > 0 ||
    withAtlassianTogether.length > 0 ||
    withBothProducts.length > 0;

  if (!hasSubscriptions) return null;

  return (
    <SectionMessage title={t('mac.licenses.enterprise.subscriptions.banner.title')}>
      <>
        <p>
          <FormattedMessage
            id="mac.licenses.enterprise.subscriptions.banner.p1"
            defaultMessage="Enterprise subscriptions are listed under your Atlassian Guard SENs and share the same billing and technical contacts. Select Show/Hide to expand or collapse the list of your Atlassian Guard SENs associated with Enterprise Subscriptions."
          />
        </p>
        {withBothProducts && (
          <SubscriptionSection
            titleId="mac.licenses.enterprise.subscriptions.banner.both"
            defaultMessage="SENs with both, Enterprise and Atlassian Together subscriptions"
            items={withBothProducts}
            isCollapsed={collapsedStates.both}
            onToggle={() => handleCollapseClick('both')}
          />
        )}
        {withEnterpriseProducts && (
          <SubscriptionSection
            titleId="mac.licenses.enterprise.subscriptions.banner.enterprise.only"
            defaultMessage="SENs with only Enterprise subscriptions"
            items={withEnterpriseProducts}
            isCollapsed={collapsedStates.enterprise}
            onToggle={() => handleCollapseClick('enterprise')}
          />
        )}
        {withAtlassianTogether && (
          <SubscriptionSection
            titleId="mac.licenses.enterprise.subscriptions.banner.at.only"
            defaultMessage="SENs with only Atlassian Together subscriptions"
            items={withAtlassianTogether}
            isCollapsed={collapsedStates.atlassianTogether}
            onToggle={() => handleCollapseClick('atlassianTogether')}
          />
        )}
      </>
    </SectionMessage>
  );
};

export default EnterpriseSubscriptionsBanner;
